import React from "react";
import { motion } from "framer-motion";
import { Calendar, Info, ShieldCheck, TrendingDown, Truck } from "lucide-react";
import { ExecutiveSummary as ExecutiveSummaryType, formatDate } from "@/lib/index";
import { IMAGES } from "@/assets/images";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
interface ExecutiveSummaryProps {
  data: ExecutiveSummaryType;
}
export function ExecutiveSummary({
  data
}: ExecutiveSummaryProps) {
  const themeIcons = [<Truck className="w-5 h-5 text-primary" />, <TrendingDown className="w-5 h-5 text-primary" />, <ShieldCheck className="w-5 h-5 text-primary" />];
  return <section className="relative w-full overflow-hidden">
      {/* Hero Background with Overlay */}
      <div className="absolute inset-0 z-0">
        <img src={IMAGES.LOGISTICS_HERO_2} alt="Logistics Hub" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-16 lg:py-24">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="max-w-5xl mx-auto space-y-12">
          {/* Header Section */}
          <motion.div variants={fadeInUp} className="space-y-6">
            <div className="flex flex-wrap items-center gap-4">
              <Badge variant="secondary" className="px-3 py-1 text-sm font-medium tracking-wide uppercase">
                Market Report
              </Badge>
              <div className="flex items-center text-muted-foreground text-sm font-mono">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate(data.date)}
              </div>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
              {data.title}
            </h1>

            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-light">
              {data.summary}
            </p>
          </motion.div>

          {/* Core Themes Grid */}
          <motion.div variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.themes.map((theme, index) => <motion.div key={index} variants={staggerItem}>
                <Card className="h-full border-border/40 bg-card/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6 space-y-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      {themeIcons[index % themeIcons.length]}
                    </div>
                    <div className="space-y-2">
                      <h3 className="font-bold text-lg text-foreground">{theme.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {theme.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>

          {/* Footer Metadata */}
          <motion.div variants={fadeInUp} className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-8 border-t border-border/40">
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <Info className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary/60" />
              <div>
                <span className="font-semibold text-foreground">Sources:</span> {data.sources}
              </div>
            </div>
            
            
          </motion.div>
        </motion.div>
      </div>

      {/* Subtle Rim Light Effect */}
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
    </section>;
}